
import { defineComponent } from 'vue';
import { GoogleMapLoaderService } from '@/shared/services/GoogleMapLoaderService';
import { GoogleMapAddressService } from '@/shared/services/GoogleMapAddressService';
import { Point } from '@/shared/models/Point';
import { Address } from '@/shared/models/Address';

const CITIES_TYPE = ['locality', 'administrative_area_level_3'];
const REGIONS_TYPE = ['locality', 'sublocality', 'postal_code', 'country', 'administrative_area_level_1', 'administrative_area_level_2'];

export default defineComponent({
  name: 'GoogleAutocomplete',
  emits: ['no-results-found', 'placechanged', 'focus', 'blur', 'error', 'keyup', 'keydown', 'keypress', 'update:modelValue'],
  props: {
    id: {
      type: String,
      required: true,
    },

    placeholder: {
      type: String,
      default: 'Start typing',
    },

    types: {
      type: Array,
      default: () => ['address', 'route'],
    },

    country: {
      type: [String, Array],
      default: null,
    },

    enableGeolocation: {
      type: Boolean,
      default: false,
    },

    geolocationOptions: {
      type: Object,
      default: null,
    },

    modelValue: {
      type: String,
      default: '',
    },
  },

  computed: {
    value: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
  },

  data() {
    return {
      place: null as null | google.maps.places.PlaceResult,
      autocomplete: null as null | google.maps.places.Autocomplete,
      geolocation: {
        geocoder: null as null | google.maps.Geocoder,
        loc: null as null | Point,
        position: null as null | any,
      },
    };
  },

  watch: {
    country: function () {
      if (this.autocomplete) {
        this.autocomplete.setComponentRestrictions({
          country: this.country === null ? ([] as string[]) : this.country,
        } as google.maps.places.ComponentRestrictions);
      }
    },
  },
  async mounted() {
    await GoogleMapLoaderService.load();

    if(!window.google || !window.google.maps) {
      return;
    }

    const options: google.maps.places.AutocompleteOptions = {};

    if (this.types) {
      options.types = [this.types];
    }

    if (this.country) {
      options.componentRestrictions = {
        country: this.country,
      } as google.maps.places.ComponentRestrictions;
    }

    const element = document.getElementById(this.id) as HTMLInputElement;
    if (element) {
      this.autocomplete = new window.google.maps.places.Autocomplete(element, options);
      if (this.autocomplete) {
        this.autocomplete.addListener('place_changed', this.onPlaceChanged);
      }
    }
  },

  methods: {
    onSearch($event) {
      this.place = null;
    },
    onPlaceChanged() {
      this.place = null;
      if (this.autocomplete) {
        this.setPlace(this.autocomplete.getPlace());
      }
    },

    onFocus() {
      this.biasAutocompleteLocation();
      this.$emit('focus');
    },

    onBlur() {
      if (!this.place || !this.modelValue) {
        this.clear();
        this.$emit('placechanged', null);
      }
      this.$emit('blur');
    },

    onKeyDown(event: any) {
      this.place = null;
      // If it's Enter
      if (event.keyCode === 13) {
        // Select all Google's dropdown DOM nodes (can be multiple)
        const googleDOMNodes = document.getElementsByClassName('pac-container');

        // Check if any of them are visible (using ES6 here for conciseness)
        const googleDOMNodeIsVisible = Array.from(googleDOMNodes).some((node: any) => node.offsetParent !== null);

        // If one is visible - preventDefault
        if (googleDOMNodeIsVisible) event.preventDefault();
      }
      this.$emit('keydown', event);
    },

    onKeyPress(event: any) {
      this.$emit('keypress', event);
    },

    onKeyUp(event: any) {
      this.$emit('keyup', event);
    },

    clear() {
      this.value = '';
      (this.$refs.autocomplete as any).value = null;
    },

    setPlace(place) {
      this.place = place;
      if (!this.place.geometry) {
        // User entered the name of a Place that was not suggested and
        // pressed the Enter key, or the Place Details request failed.
        this.$emit('no-results-found', this.place, this.id);
        return;
      }

      if (this.place.address_components !== undefined) {
        // return returnData object and PlaceResult object
        this.$emit('placechanged', this.formatResult(this.place), this.place, this.id);

        //update autocompleteText then emit change event
        const element = document.getElementById(this.id) as HTMLInputElement;
        if (element) {
          this.value = element.value;
        }
      }
    },

    updateCoordinates(value: Point) {
      if (!value || !(value.lat || value.lng)) return;
      if (!this.geolocation.geocoder) this.geolocation.geocoder = new window.google.maps.Geocoder();
      this.geolocation.geocoder.geocode({ location: value }, (results, status) => {
        if (status === 'OK') {
          results = this.filterGeocodeResultTypes(results);
          if (results[0]) {
            this.$emit('placechanged', this.formatResult(results[0]), results[0], this.id);
            this.value = results[0].formatted_address;
          } else {
            this.$emit('error', 'no result for provided coordinates');
          }
        } else {
          this.$emit('error', 'error getting address from coords');
        }
      });
    },

    geolocate() {
      this.updateGeolocation((geolocation: Point) => {
        this.updateCoordinates(geolocation);
      });
    },

    updateGeolocation(callback: (geolocation: Point, position: any) => void) {
      if (navigator.geolocation) {
        const options = {};
        if (this.geolocationOptions) Object.assign(options, this.geolocationOptions);
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const geolocation: Point = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            this.geolocation.loc = geolocation;
            this.geolocation.position = position;

            if (callback) {
              callback(geolocation, position);
            }
          },
          (err) => {
            this.$emit('error', 'Cannot get Coordinates from navigator', err);
          },
          options,
        );
      }
    },

    biasAutocompleteLocation() {
      if (this.enableGeolocation) {
        this.updateGeolocation((geolocation, position) => {
          const circle = new window.google.maps.Circle({
            center: geolocation,
            radius: position.coords.accuracy,
          });
          if (this.autocomplete) {
            this.autocomplete.setBounds(circle.getBounds());
          }
        });
      }
    },

    formatResult(place: google.maps.GeocoderResult | google.maps.places.PlaceResult): Address {
      return GoogleMapAddressService.getAddress(place);
    },

    filterGeocodeResultTypes(results: any) {
      if (!results || !this.types) return results;
      const output = [] as any[];
      let types = [this.types];
      if (types.includes('(cities)')) types = types.concat(CITIES_TYPE);
      if (types.includes('(regions)')) types = types.concat(REGIONS_TYPE);

      for (const r of results) {
        for (const t of r.types) {
          if (types.includes(t)) {
            output.push(r);
            break;
          }
        }
      }
      return output;
    },
  },
});
